import _ from 'lodash';
import { createSelector } from 'reselect';
import {
  getDischargeModuleForCurrentLocale,
  getCurrentModule,
  getLearnModulesForCurrentLocale
} from './modules';
import {
  getLessonsWithOrderedPages,
  getLessonIdToPageCountMap,
  getDischargeLessons
} from './lessons';
import {
  getProgressObjects,
  getLessons,
  getLessonPages,
  getModuleIdToLessonPageIdsMap,
  getDischargeProgressObjects,
  getCurrentLessonSlug
} from './core';

const getCompletedLessonPageIds = createSelector(
  getProgressObjects,
  progress => _.map(progress, p => p.lessonPageId)
);

const getStartedLessonIds = createSelector(
  getDischargeProgressObjects,
  progress => _.map(progress, p => p.lessonId)
);

const getLessonProgressForCurrentProfile = createSelector(
  getLessons,
  getStartedLessonIds,
  getCompletedLessonPageIds,
  (lessons, startedLessonIds, completedLessonPageIds) =>
    _.reduce(
      lessons,
      (result, lesson) => {
        const allPageIdsForLesson = lesson.pages;

        const numLessonPagesCompleted = _.intersection(
          allPageIdsForLesson,
          completedLessonPageIds
        ).length;

        const numTotal = allPageIdsForLesson.length;

        let lessonProgress = 0;
        if (numLessonPagesCompleted > 0 && numTotal > 0) {
          lessonProgress = (numLessonPagesCompleted / numTotal) * 100;
          // Small hack to mark a started lesson as in progress
        } else if (_.includes(startedLessonIds, lesson.id)) {
          lessonProgress = 1;
        }

        return { ...result, [lesson.id]: lessonProgress };
      },
      {}
    )
);

const getModuleProgressForCurrentProfile = createSelector(
  getModuleIdToLessonPageIdsMap,
  getCompletedLessonPageIds,
  (moduleIdToLessonPageIdsMap, completedLessonPageIds) =>
    _.reduce(
      moduleIdToLessonPageIdsMap,
      (result, lessonPageIds, moduleId) => {
        const numLessonPagesCompleted = _.intersection(
          lessonPageIds,
          completedLessonPageIds
        ).length;
        const numLessonPagesTotal = lessonPageIds.length;

        const progressPercentage =
          numLessonPagesCompleted === 0
            ? 0
            : (numLessonPagesCompleted / numLessonPagesTotal) * 100;

        return { ...result, [moduleId]: progressPercentage };
      },
      {}
    )
);

const getLessonIdToMostRecentlyCompletedPageNumberMap = createSelector(
  getLessonsWithOrderedPages,
  getCompletedLessonPageIds,
  (lessons, completedPageIds) =>
    _.mapValues(lessons, lesson =>
      _.reduce(
        lesson.pages,
        (res, page) =>
          _.includes(completedPageIds, page.id) && page.displayOrder > res
            ? page.displayOrder
            : res,
        0
      )
    )
);

const getLessonIdToNextPageNumberMap = createSelector(
  getLessons,
  getLessonIdToMostRecentlyCompletedPageNumberMap,
  getLessonIdToPageCountMap,
  (
    lessons,
    lessonIdToMostRecentlyCompletedPageNumberMap,
    lessonIdToPageCountMap
  ) =>
    _.mapValues(lessons, lesson => {
      const mostRecentlyCompletedPageNum =
        lessonIdToMostRecentlyCompletedPageNumberMap[lesson.id];
      const totalPagesInLesson = lessonIdToPageCountMap[lesson.id];

      // If the lesson is complete always start at the first page
      if (mostRecentlyCompletedPageNum === totalPagesInLesson) {
        return 1;
      }

      return mostRecentlyCompletedPageNum + 1;
    })
);

const getLessonPageIdToIsReadMap = createSelector(
  getLessonPages,
  getCompletedLessonPageIds,
  (lessonPages, completedPageIds) =>
    _.mapValues(lessonPages, lessonPage =>
      _.includes(completedPageIds, lessonPage.id)
    )
);

const getDischargeLessonStartOrder = createSelector(
  getDischargeLessons,
  getProgressObjects,
  getDischargeProgressObjects,
  (lessons, progressObjects, dischargeProgressObjects) => {
    const sortedProgressObjects = _.sortBy(progressObjects, 'createdAt');
    const sortedDischargeProgressObjects = _.sortBy(
      dischargeProgressObjects,
      'createdAt'
    );

    return _.chain(lessons)
      .map(lesson => {
        let firstProgressObjectForLesson = _.find(
          sortedDischargeProgressObjects,
          progress => lesson.id === progress.lessonId
        );

        if (!firstProgressObjectForLesson) {
          firstProgressObjectForLesson = _.find(
            sortedProgressObjects,
            progress => _.includes(lesson.pages, progress.lessonPageId)
          );
        }

        return {
          lessonId: lesson.id,
          startDate: firstProgressObjectForLesson
            ? firstProgressObjectForLesson.createdAt
            : null
        };
      })
      .sortBy('startDate')
      .value();
  }
);

const getDischargeProgressForCurrentProfile = createSelector(
  getDischargeModuleForCurrentLocale,
  getModuleProgressForCurrentProfile,
  getStartedLessonIds,
  (dischargeModule, moduleProgress, startedLessonIds) =>
    // Small hack to give you progress if you've started any discharge lesson
    startedLessonIds.length ? 1 : moduleProgress[dischargeModule.id]
);

const getCurrentProgress = createSelector(
  ({ progress }) => progress.all,
  progress =>
    progress.map(module => ({
      ...module,
      topics:
        module && Array.isArray(module.topics)
          ? module.topics.map(({ topicId }) => topicId)
          : []
    }))
);

const mapModuleToProgression = (module, progress) => {
  // Find the matching id
  const progression = progress.find(({ chapterId }) => chapterId === module.ID);
  // Map
  const lessons = module.lessons.map(lesson => {
    const completedTopics = [];
    return {
      ...lesson,
      topics: lesson.topics.map(topic => {
        const completed =
          progression && progression.topics
            ? progression.topics.includes(topic.ID)
            : false;

        if (completed) {
          completedTopics.push(topic.ID);
        }

        return {
          ...topic,
          completed
        };
      }),
      completion: (completedTopics.length / lesson.topics.length) * 100 || 0
    };
  });

  // Get completion for module
  const weight = lessons.length * 100;
  const completionModule = lessons
    .map(lesson => lesson.completion)
    .reduce((total, value) => total + (value / weight) * 100, 0);

  // ensure we have a whole percentage number (fix for chapter 2 displaying as 99.99999999999997%)
  const completion = Math.round(completionModule);

  return {
    ...module,
    lessons,
    completion
  };
};

const getCurrentModuleWithLessonsAndProgress = createSelector(
  getCurrentModule,
  getCurrentProgress,
  (module, progress) => {
    if (!module) {
      return undefined;
    }

    return mapModuleToProgression(module, progress);
  }
);

const getCurrentLessonWithProgress = createSelector(
  getCurrentModuleWithLessonsAndProgress,
  getCurrentLessonSlug,
  (currentModule, lessonSlug) => {
    if (typeof currentModule !== 'undefined') {
      return currentModule.lessons[parseInt(lessonSlug, 10) - 1];
    }
    return null;
  }
);

const getAllModulesWithProgress = createSelector(
  getLearnModulesForCurrentLocale,
  getCurrentProgress,
  (modules, progress) => {
    if (!modules) {
      return undefined;
    }
    return modules.map(module => mapModuleToProgression(module, progress));
  }
);

export {
  getProgressObjects,
  getCompletedLessonPageIds,
  getModuleProgressForCurrentProfile,
  getLessonProgressForCurrentProfile,
  getLessonIdToNextPageNumberMap,
  getLessonPageIdToIsReadMap,
  getDischargeLessonStartOrder,
  getDischargeProgressForCurrentProfile,
  getCurrentModuleWithLessonsAndProgress,
  getCurrentProgress,
  getAllModulesWithProgress,
  getCurrentLessonWithProgress
};



// WEBPACK FOOTER //
// ./src/selectors/progress.js